import { useCallback, useEffect, useState } from 'react';

import { Input, Skeleton } from '@storybook';
import './add-access-code.scss';
import { AdditionalFlag } from '../additional-flag';
import { IRecipient, IsEditViewOnly, ISignedDocInbox } from 'views/signed-doc-inbox';
import {
	IsRecipientLoading,
	useRecipients,
	AccessCodeErrorsState,
} from 'views/edit-recipient/stores';
import { useRecoilValue, useSetRecoilState } from 'recoil';


interface IProps {
	recipient: IRecipient;
	index: number;
	inbox: ISignedDocInbox;
}

const ACCESS_CODE_REGEX = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,50}$/;

const AddAccessCode = ({ recipient, index, inbox }: IProps) => {
	const [isAccessCodeEnabled, setIsAccessCodeEnabled] = useState(
		recipient.isAccessCodeEnabled || false
	);
	const [accessCode, setAccessCode] = useState(recipient.accessCode || '');
	const [isAccessCodeError, setIsAccessCodeError] = useState(false);

	const isRecipientLoading = useRecoilValue(IsRecipientLoading);
	const isEditViewOnly = useRecoilValue(IsEditViewOnly);
	const { setRecipientsWithValue } = useRecipients();
	const setAccessCodeErrors = useSetRecoilState(AccessCodeErrorsState);

	const handleRegenerateAccessCode = useCallback(() => {
		function generateValidAccessCode() {
			const specials = '!@#$%^&*()_+-={}[]:;<>,.?/~';
			const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
			const digits = '0123456789';
			const allNonSpecials = letters + digits;

			const minLen = 8;
			const maxLen = 12;
			const len = Math.floor(Math.random() * (maxLen - minLen + 1)) + minLen;

			const code = [
				letters[Math.floor(Math.random() * letters.length)], // Ensure 1 letter
				digits[Math.floor(Math.random() * digits.length)], // Ensure 1 digit
				specials[Math.floor(Math.random() * specials.length)], // Exactly 1 special char
			];

			while (code.length < len) {
				code.push(
					allNonSpecials[Math.floor(Math.random() * allNonSpecials.length)]
				);
			}

			// Shuffle the characters
			for (let i = code.length - 1; i > 0; i--) {
				const j = Math.floor(Math.random() * (i + 1));
				[code[i], code[j]] = [code[j], code[i]];
			}

			return code.join('');
		}

		let newCode = '';
		do {
			newCode = generateValidAccessCode();
		} while (!ACCESS_CODE_REGEX.test(newCode));

		setAccessCode(newCode);
		setRecipientsWithValue('accessCode' as keyof IRecipient, newCode, index);
		setIsAccessCodeError(false);
		
		// Clear error when regenerating (always generates valid code)
		const recipientId = String(recipient._id);
		setAccessCodeErrors(prev => ({
			...prev,
			[recipientId]: false,
		}));
	}, [index, setRecipientsWithValue, recipient._id, setAccessCodeErrors]);

	const handleAccessCodeToggle = useCallback(
		(checked: boolean) => {
			setIsAccessCodeEnabled(checked);
			const recipientId = String(recipient._id);
			if (checked) {
				handleRegenerateAccessCode();
				setRecipientsWithValue('isAccessCodeEnabled', true, index);
				setIsAccessCodeError(false);
				// Clear error when enabling (regenerate creates valid code)
				setAccessCodeErrors(prev => ({
					...prev,
					[recipientId]: false,
				}));
			} else {
				setAccessCode('');
				setRecipientsWithValue('isAccessCodeEnabled', false, index);
				setRecipientsWithValue('accessCode', undefined, index);
				setIsAccessCodeError(false);
				// Clear error when disabling
				setAccessCodeErrors(prev => ({
					...prev,
					[recipientId]: false,
				}));
			}
		},
		[handleRegenerateAccessCode, index, setRecipientsWithValue, recipient._id, setAccessCodeErrors]
	);

	const handleAccessCodeChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const value = event.target.value;
			setAccessCode(value);
			setRecipientsWithValue('accessCode', value, index);
			const hasError = !ACCESS_CODE_REGEX.test(value);
			setIsAccessCodeError(hasError);
			
			// Update global access code errors state
			const recipientId = String(recipient._id);
			setAccessCodeErrors(prev => ({
				...prev,
				[recipientId]: hasError,
			}));
		},
		[index, setRecipientsWithValue, recipient._id, setAccessCodeErrors]
	);

	useEffect(() => {
		if (!isRecipientLoading) {
			setAccessCode(recipient?.accessCode || '');
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isRecipientLoading, recipient?.accessCode]);

	// Sync local state with prop when recipient.isAccessCodeEnabled changes
	useEffect(() => {
		setIsAccessCodeEnabled(recipient.isAccessCodeEnabled || false);
	}, [recipient.isAccessCodeEnabled]);

	// Validate access code on mount and when it changes
	useEffect(() => {
		const recipientId = String(recipient._id);
		if (recipient.isAccessCodeEnabled && accessCode) {
			const hasError = !ACCESS_CODE_REGEX.test(accessCode);
			setIsAccessCodeError(hasError);
			setAccessCodeErrors(prev => ({
				...prev,
				[recipientId]: hasError,
			}));
		} else if (recipient.isAccessCodeEnabled && !accessCode) {
			// If access code is enabled but empty, it's an error
			setIsAccessCodeError(true);
			setAccessCodeErrors(prev => ({
				...prev,
				[recipientId]: true,
			}));
		} else {
			// If access code is not enabled, clear any errors
			setAccessCodeErrors(prev => ({
				...prev,
				[recipientId]: false,
			}));
		}
	}, [recipient.isAccessCodeEnabled, recipient._id, accessCode, setAccessCodeErrors]);

	return (
		<div className="add-recipient__access-code-container">
			<div className="add-recipient__access-code-body">
				<AdditionalFlag
					value={isAccessCodeEnabled}
					handleChange={handleAccessCodeToggle}
					heading={'Recipient Access Code'}
					isDisabled={inbox.authType !== 'facial'}
					isViewOnly={isEditViewOnly}
					description={
						'Recipient attempting to access will need to enter this code before accessing the document.'
					}
				/>
				{isAccessCodeEnabled && (
					<>
						{!isRecipientLoading ? !isEditViewOnly ? (
							
							<Input
								handleChange={handleAccessCodeChange}
								inputType="text"
								placeholder="Enter Access Code"
								label={
									<div className="add-recipient__access-code-regen">
										Access Code
										<span
											className="add-recipient__access-code-regen-btn"
											onClick={handleRegenerateAccessCode}
										>
											Regenerate New Access Code
										</span>
									</div>
								}
								value={accessCode}
								isError={isAccessCodeError}
								errorMessage={
									!accessCode?.trim()
										? 'Please enter access code'
										: accessCode.length < 8
										? 'Access code must be at least 8 characters'
										: accessCode.length > 50
										? 'Access code must be at most 50 characters'
										: !ACCESS_CODE_REGEX.test(accessCode)
										? 'Must contain at least 1 letter, 1 number, 1 special character'
										: ''
								}
								id={`${recipient._id}-accessCode`}
								autoComplete="off"
								inputName="accessCode"
							/>
						) :  <div className='add-recipient__access-code-view'>
							<p className='access-code-view-label'>Access Code</p> <span>:</span> <p className='access-code-view-value'>{accessCode}</p>
						</div> : (
							<Skeleton height={36} width={300} />
						)}

						{!isEditViewOnly && <div className="add-recipient__access-code-info">
							<i className="ri-information-line recipient__access-code-info-icon"></i>
							<p className="add-recipient__access-code-info-msg">
								Make sure to share this code securely with the signer or viewer.
								Without it, they won’t be able to proceed.
							</p>
						</div>}
					</>
				)}
			</div>
		</div>
	);
};

export default AddAccessCode;
